import EventHandler from 'bootstrap/js/src/dom/event-handler';

export const initBootstrapAdditions = () => {
  initBsDropdown();
  initBsTabs();
  initBsTooltips();
  initBsModal();
  initBsOffcanvas();
};

// == DROPDOWN
function initBsDropdown() {
  const selector = '.js-dropdown-appendTo-body, .swiper';
  let dropdownToggle;
  let dropdownMenu;

  const toggleShownDropdownToBody = event => {
    const isShown = event.type === 'show.bs.dropdown';
    if (isShown) {
      if (dropdownToggle) dropdownToggle.click();
      dropdownToggle = event.target;
      dropdownMenu = dropdownToggle.parentElement.querySelector('.dropdown-menu');
      if (dropdownMenu) {
        dropdownMenu.classList.add('js-appendTo-body');
        document.body.appendChild(dropdownMenu);
      }
    } else {
      if (dropdownMenu) {
        dropdownMenu.classList.remove('js-appendTo-body');
        dropdownToggle.parentElement.appendChild(dropdownMenu);
        dropdownMenu = null;
        dropdownToggle = null;
      }
    }
  };

  EventHandler.on(document, 'show.bs.dropdown', selector, toggleShownDropdownToBody);
  EventHandler.on(document, 'hide.bs.dropdown', selector, toggleShownDropdownToBody);
}

// == TABS
function handleTabState(event) {
  const targetSelector = event.target.dataset.bsTarget;
  const isShowing = event.type === 'shown.bs.tab';
  const tabPanes = document.querySelectorAll(targetSelector);

  if (tabPanes.length <= 1) return;

  tabPanes.forEach(pane => {
    const isActive = pane.classList.contains('active');

    if (isShowing && !isActive) {
      const context = pane.classList.contains('widget-wrapper')
        ? pane.closest('.page-content')
        : pane.closest('.tab-content');

      const activePane = context?.querySelector('.tab-pane.active');

      if (activePane && ![...tabPanes].some(el => el.isEqualNode(activePane))) {
        activePane.classList.remove('active', 'show');
      }

      pane.classList.add('active');
      if (pane.classList.contains('fade')) {
        setTimeout(() => pane.classList.add('show'), 350);
      }
    }

    if (!isShowing && isActive) {
      if (pane.classList.contains('fade')) {
        pane.classList.remove('show');
        setTimeout(() => pane.classList.remove('active'), 350);
      } else {
        pane.classList.remove('active');
      }
    }
  });
}

function fixShownNestedTabs(bsTarget) {
  document.querySelectorAll(bsTarget).forEach(container => {
    container.querySelectorAll('.nav-tabs .nav-link.active').forEach(activeLink => {
      const nestedTarget = activeLink.dataset.bsTarget;
      container.querySelectorAll(nestedTarget).forEach(nestedPane => {
        if (!nestedPane.classList.contains('active')) {
          nestedPane.classList.add('active', 'show');
        }
      });
    });
  });
}

function toggleVisibleByCategory({target}) {
  const {category: currentCategory} = target.dataset;
  const navbar = target.closest('.widget-tabs');
  const container = navbar?.dataset.bsTarget && document.querySelector(navbar.dataset.bsTarget);
  if (!container) return;

  const cards = container.querySelectorAll('[data-category]');
  const fadeOutDuration = 300;
  const fadeInStagger = 80;

  const fadeOut = el =>
    new Promise(resolve => {
      el.classList.remove('fade-in');
      el.classList.add('fade-out');
      setTimeout(() => {
        el.classList.add('d-none');
        el.classList.remove('fade-out');
        resolve();
      }, fadeOutDuration);
    });

  const fadeIn = (el, index) => {
    setTimeout(() => {
      el.classList.remove('d-none');
      void el.offsetWidth;
      el.classList.add('fade-in');
      setTimeout(() => {
        el.classList.remove('fade-in');
      }, fadeOutDuration);
    }, index * fadeInStagger);
  };

  const hideAndShow = (toHide, toShow) => {
    const hidePromises = Array.from(toHide).map(fadeOut);
    Promise.all(hidePromises).then(() => {
      Array.from(toShow).forEach((el, index) => fadeIn(el, index));
    });
  };

  if (currentCategory === 'all') {
    hideAndShow(cards, cards);
  } else {
    const toHide = Array.from(cards).filter(el => {
      const {category} = el.dataset;
      return category !== currentCategory && !el.classList.contains('d-none');
    });

    const toShow = Array.from(cards).filter(el => el.dataset.category === currentCategory);

    hideAndShow(toHide, toShow);
  }
}

function initBsTabs() {
  // Multiple tabs
  document.querySelectorAll('[data-bs-toggle="tab"]').forEach(tabLink => {
    tabLink.addEventListener('shown.bs.tab', event => {
      handleTabState(event);
      fixShownNestedTabs(event.target.dataset.bsTarget);
    });

    tabLink.addEventListener('hidden.bs.tab', handleTabState);
  });

  EventHandler.on(document, 'shown.bs.tab', '[data-bs-toggle="tab"][data-category]', toggleVisibleByCategory);

  document.addEventListener('shown.bs.tab', event => {
    if (!event.target.dataset.tabAnchor) return;

    const url = new URL(window.location.href);
    url.searchParams.set('tab', event.target.dataset.tabAnchor);
    window.history.pushState({}, '', url);
  });

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('tab')) {
    const tabAnchor = urlParams.get('tab');
    const tab = document.querySelector(`[data-tab-anchor="${tabAnchor}"]`);
    if (!tab) return;

    setTimeout(() => {
      if (tab.classList.contains('active')) EventHandler.trigger(tab, 'shown.bs.tab');
      else tab.click();
    }, 100);
  }
}

// == TOOLTIPS
function initBsTooltips() {
  document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
    new bootstrap.Tooltip(el, {
      boundary: document.body,
      trigger: 'hover',
      //offset: [20, 20]
    });
  });

  EventHandler.on(document, 'show.bs.tooltip', 'body', event => {
    const {bsTitle, bsTitleActive} = event.target.dataset;
    const btnToggle = event.target.querySelector('.btn');
    if (bsTitleActive && btnToggle) {
      const title = btnToggle.classList.contains('active') ? bsTitleActive : bsTitle;
      const bsTooltip = bootstrap.Tooltip.getOrCreateInstance(event.target);
      bsTooltip && bsTooltip.setContent({'.tooltip-inner': title});
    }
  });

  EventHandler.on(
    document,
    'click.bs.button.data-api',
    '[data-bs-toggle="tooltip"][data-bs-title-active] .btn',
    event => {
      const tooltip = event.target.closest('[data-bs-toggle="tooltip"]');
      if (tooltip) bootstrap.Tooltip.getOrCreateInstance(tooltip).hide();
    }
  );
}

// == MODAL
function initBsModal() {
  document.addEventListener('show.bs.modal', ({relatedTarget, target}) => {
    if (relatedTarget) {
      relatedTarget.classList.add('show');

      relatedTarget.closest('.dropdown')?.addEventListener(
        'hide.bs.dropdown',
        e => {
          if (relatedTarget.classList.contains('show')) {
            e.preventDefault();
          }
        },
        {once: true}
      );

      target.addEventListener(
        'hidden.bs.modal',
        () => {
          setTimeout(() => {
            relatedTarget.classList.remove('show');
          }, 0);
        },
        {once: true}
      );
    }

    const {bsTabTarget} = relatedTarget.dataset;
    if (!bsTabTarget) return;

    const tab = target.querySelector(`[data-bs-toggle="tab"][data-bs-target="${bsTabTarget}"]`);
    if (tab) tab.click();
  });
}

// == Offcanvas
function initBsOffcanvas() {
  const toggleShownOffcanvas = event => {
    const isShown = event.type === 'show.bs.offcanvas';
    document.body.classList.toggle('offcanvas-open', isShown);
  };

  document.addEventListener('show.bs.offcanvas', toggleShownOffcanvas);
  document.addEventListener('show.bs.offcanvas', toggleShownOffcanvas);
}
